import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import {
  X,
  Loader2,
  Calendar,
  Shield,
  Eye,
  Lock,
  Check,
} from "lucide-react";

const OutlookSetupGuide = ({ isOpen, onClose, userId, onConnected }) => {
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API}/calendar/outlook/connect/${userId}`);
      if (response.data.authorization_url) {
        const width = 500;
        const height = 650;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const popup = window.open(
          response.data.authorization_url,
          "OutlookAuth",
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
        );

        // Listen for message from popup
        const messageHandler = (event) => {
          if (event.data?.type === "outlook_auth") {
            window.removeEventListener("message", messageHandler);
            clearInterval(pollTimer);
            setConnecting(false);
            if (event.data.status === "success") {
              toast.success("Outlook connected successfully!");
              onConnected?.();
              onClose();
            } else {
              toast.error("Connection failed. Please try again.");
            }
          }
        };
        window.addEventListener("message", messageHandler);

        const pollTimer = setInterval(() => {
          if (popup?.closed) {
            clearInterval(pollTimer);
            window.removeEventListener("message", messageHandler);
            setConnecting(false);
            // Check status after popup closes
            onConnected?.();
            onClose();
          }
        }, 500);

        setTimeout(() => {
          clearInterval(pollTimer);
          window.removeEventListener("message", messageHandler);
          setConnecting(false);
        }, 120000);
      }
    } catch (error) {
      console.error("Error connecting Outlook:", error);
      toast.error("Failed to start connection. Please try again.");
      setConnecting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

        <motion.div
          className="glass-card relative z-10 w-full max-w-md rounded-2xl overflow-hidden border border-[#0078d4]/30"
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="outlook-setup-guide"
        >
          {/* Header */}
          <div className="p-6 border-b border-white/10 text-center bg-gradient-to-b from-[#0078d4]/10 to-transparent">
            <div className="inline-flex p-3 rounded-2xl bg-[#0078d4]/10 border border-[#0078d4]/20 mb-4">
              <Calendar className="h-8 w-8 text-[#0078d4]" />
            </div>
            <h2 className="text-xl text-white font-semibold">
              Connect Your Calendar
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Sync your Outlook calendar to see meetings and prep briefs
            </p>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* What you get */}
          <div className="p-6 space-y-3">
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-lg bg-ism-teal/10">
                <Calendar className="h-4 w-4 text-ism-teal" />
              </div>
              <div>
                <p className="text-sm text-white font-medium">See upcoming meetings</p>
                <p className="text-xs text-gray-500">Your calendar syncs automatically</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-lg bg-ism-teal/10">
                <Eye className="h-4 w-4 text-ism-teal" />
              </div>
              <div>
                <p className="text-sm text-white font-medium">AI meeting prep briefs</p>
                <p className="text-xs text-gray-500">Get prospect intel before every call</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-lg bg-ism-teal/10">
                <Shield className="h-4 w-4 text-ism-teal" />
              </div>
              <div>
                <p className="text-sm text-white font-medium">Read-only access</p>
                <p className="text-xs text-gray-500">ISM can only view your calendar, never modify it</p>
              </div>
            </div>
          </div>

          {/* Connect button */}
          <div className="p-6 pt-2 space-y-3">
            <Button
              onClick={handleConnect}
              disabled={connecting}
              className="w-full bg-[#0078d4] hover:bg-[#106ebe] text-white font-medium py-6 text-base"
              data-testid="outlook-connect-btn"
            >
              {connecting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Connecting...
                </>
              ) : (
                <>
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                  </svg>
                  Sign in with Microsoft
                </>
              )}
            </Button>
            <p className="text-xs text-gray-600 text-center">
              Works with Outlook.com, Office 365, and Hotmail
            </p>
          </div>

          {/* Privacy note */}
          <div className="px-6 pb-5">
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-white/5 border border-white/5">
              <Lock className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
              <p className="text-xs text-gray-500">
                ISM requests calendar read-only access. Your data is never shared.
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OutlookSetupGuide;
